import { TabsContainer, type TabItem } from '@/components/TabsContainer';
import { BUYER_OPTIONS } from '@client/hooks/useShopInsuranceState';
import type { PaymentOption } from '@client/components/ShopInsuranceModal';
import type { ReactNode } from 'react';

interface ShopInsuranceTabsWrapperProps {
  hasPartnerWithDemographics: boolean;
  isMarriedOrPartnered: boolean;
  paymentOption: PaymentOption;
  selectedTab: ShopInsuranceTab;
  selectedBuyerOption: {
    key: '1-buyer' | '2-buyer';
    label: string;
    maritalClass: string;
  };
  tabContent: ReactNode;
  handleTabChangeWithFetch: (tabValue: string) => void;
  handleBuyerOptionChange: (buyerKey: '1-buyer' | '2-buyer') => void;
  featureFlags?: FeatureFlags;
}

export function ShopInsuranceTabsWrapper({
  hasPartnerWithDemographics,
  isMarriedOrPartnered,
  paymentOption,
  selectedTab,
  selectedBuyerOption,
  tabContent,
  handleTabChangeWithFetch,
  handleBuyerOptionChange,
  featureFlags,
}: ShopInsuranceTabsWrapperProps) {
  if (isMarriedOrPartnered && !['Monthly', 'Annually'].includes(paymentOption)) {
    const tabs: TabItem[] = [
      {
        value: 'single',
        label: 'Single Policy',
        content: tabContent,
        dataTestId: 'shop-insurance-tabs-single',
      },
    ];

    if (hasPartnerWithDemographics) {
      tabs.push({
        value: 'joint',
        label: 'Joint Policy',
        content: tabContent,
        dataTestId: 'shop-insurance-tabs-joint',
      });
    }

    // Only show Couples Discount tab if Lincoln feature flag is enabled
    if (featureFlags?.quoteApplyLincolnFinancial) {
      tabs.push({
        value: 'couples',
        label: 'Couples Discount',
        content: tabContent,
        dataTestId: 'shop-insurance-tabs-couples',
      });
    }

    // If only one tab, don't show tabs UI - just render content
    if (tabs.length === 1) {
      return <>{tabContent}</>;
    }

    return (
      <TabsContainer
        tabs={tabs}
        value={selectedTab}
        defaultValue={hasPartnerWithDemographics ? 'joint' : 'single'}
        onValueChange={handleTabChangeWithFetch}
        tabsListClassName="w-max"
      />
    );
  }

  // 1-buyer/2-buyer tabs (for Monthly/Annually payment options when married/partnered)
  if (isMarriedOrPartnered && ['Monthly', 'Annually'].includes(paymentOption)) {
    const tabs: TabItem[] = [
      {
        value: '1-buyer',
        label: BUYER_OPTIONS['1-buyer'].label,
        content: tabContent,
      },
      {
        value: '2-buyer',
        label: BUYER_OPTIONS['2-buyer'].label,
        content: tabContent,
      },
    ];

    return (
      <TabsContainer
        tabs={tabs}
        value={selectedBuyerOption.key}
        defaultValue="1-buyer"
        onValueChange={value => handleBuyerOptionChange(value as '1-buyer' | '2-buyer')}
        tabsListClassName="w-max"
      />
    );
  }

  // No tabs - just render content
  return <>{tabContent}</>;
}
